import { SalesAppointment } from '@sunrun/experience-ui-components';

/** US-style grouping for numeric UI (e.g. 1,000). Safe for integers and decimals. */
const formatDisplayNumber = (value: number | string): string => {
  let val = Number(value);
  if (!Number.isFinite(val)) {
    return '0';
  }
  return new Intl.NumberFormat('en-US').format(val);
};

const formatCurrency = (
  value?: number | null,
  decimals = 0,
  includeDollarSign = true,
) => {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  const currencyValue = Number(value).toFixed(decimals);
  const negative = currencyValue.startsWith('-') ? '-' : '';
  return `${negative}${includeDollarSign ? '$' : ''}${currencyValue
    .replace(/^(?:-)/, '')
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`;
};

const formatDateMMDDYYYY = (dateString?: string | null) => {
  if (!dateString) {
    return 'N/A';
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  } catch (error) {
    return 'Invalid date';
  }
};

/** Unix time in ms → "Fri Apr 25, 2025" */
const formatUnixTimestampSeconds = (
  timestampSeconds?: string | number | null,
) => {
  if (
    timestampSeconds === null ||
    timestampSeconds === undefined ||
    timestampSeconds === ''
  ) {
    return 'N/A';
  }
  const n =
    typeof timestampSeconds === 'string'
      ? Number(timestampSeconds)
      : timestampSeconds;
  if (!Number.isFinite(n)) {
    return 'Invalid date';
  }
  const date = new Date(n * 1000);
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }
  const weekday = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
  }).format(date);
  const monthDayYear = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
  return `${weekday} ${monthDayYear}`;
};

/** Normalize date string to full ISO for API. Handles date-only (YYYY-MM-DD) or full ISO. */
const dateToISO = (dateString: string, endOfDay: boolean): string => {
  if (!dateString) {
    return '';
  }
  if (dateString.includes('T')) {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) {
      return '';
    }
    if (endOfDay) {
      d.setUTCHours(23, 59, 59, 999);
    } else {
      d.setUTCHours(0, 0, 0, 0);
    }
    return d.toISOString();
  }
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return '';
  }
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  if (isNaN(date.getTime())) {
    return '';
  }
  if (endOfDay) {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date.toISOString();
};

// Format as "H:MM AM/PM, MMM DD.YY"
const formatDate = (dateString?: string | null) => {
  if (!dateString) {
    return 'N/A';
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const timeString = formatTime(dateString);
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: '2-digit',
    });

    const formattedDate = dateFormatter.format(date).replace(', ', '.');

    return `${timeString}, ${formattedDate}`;
  } catch (error) {
    return 'Invalid date';
  }
};

// Format as "H:MM AM/PM" or "H AM/PM" if minutes are 00
const formatTime = (dateString?: string | null) => {
  if (!dateString) {
    return 'N/A';
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const timeString = formatter.format(date).replace(':00', '');

    return timeString;
  } catch (error) {
    return 'Invalid date';
  }
};

const formatAppointmentDateTime = (
  appointment: SalesAppointment,
  timezone?: string,
) => {
  const timeOptions = {
    hour: 'numeric',
    minute: 'numeric',
    timeZone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  } as Intl.DateTimeFormatOptions;

  const dateOptions = {
    year: '2-digit',
    month: 'short',
    day: 'numeric',
    timeZone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  } as Intl.DateTimeFormatOptions;

  const startTime = new Intl.DateTimeFormat('en-US', timeOptions)
    .format(new Date(appointment.dateTime))
    .replace(':00', '');
  const endTime = new Intl.DateTimeFormat('en-US', timeOptions)
    .format(new Date(appointment.endTime))
    .replace(':00', '');
  const dateFormatted = new Intl.DateTimeFormat('en-US', dateOptions)
    .format(new Date(appointment.dateTime))
    .replace(', ', '.');

  return `${startTime} - ${endTime}, ${dateFormatted}`;
};

//  Input format: "2026-02-02T18:24:43.940Z" Output: "Feb 2, 2026"
const formatDateReadable = (dateString: string) => {
  const date = new Date(dateString);
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return formattedDate;
};

/** Card subtitle: "6189 W Verdugo Ave, Burbank, CA". Falls back to full address. */
const formatPipelineCardAddress = ({
  street,
  city,
  state,
  address,
}: {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  address?: string | null;
}) => {
  const trimmedStreet = street?.trim();
  const trimmedCity = city?.trim();
  const streetAndCity = [trimmedStreet, trimmedCity].filter(Boolean).join(', ');
  const trimmedState = state?.trim();

  if (streetAndCity && trimmedState) {
    return `${streetAndCity}, ${trimmedState}`;
  }
  if (streetAndCity) {
    return streetAndCity;
  }
  if (trimmedState) {
    return trimmedState;
  }

  return address?.trim() ?? '';
};

export {
  formatCurrency,
  formatDisplayNumber,
  formatDate,
  formatTime,
  formatAppointmentDateTime,
  formatDateMMDDYYYY,
  formatUnixTimestampSeconds,
  dateToISO,
  formatDateReadable,
  formatPipelineCardAddress,
};
